import { useState, useEffect } from 'react'
import { X, Settings, Loader2 } from 'lucide-react'
import { rfpApi } from '@/api/client'
import { showToast } from '@/store/uiStore'
import type { RfpReferenceDoc, RfpTeamMember, RfpDefaultRouting } from '../types'
import { ROUTING_CATEGORIES } from '../types'
import { ReferenceUploader } from './ReferenceUploader'
import { TeamManager } from './TeamManager'

function unwrapList(res: any): any[] {
  const d = res?.data?.data || res?.data || []
  return Array.isArray(d) ? d : []
}

interface RfpSettingsProps {
  open: boolean
  onClose: () => void
}

export function RfpSettings({ open, onClose }: RfpSettingsProps) {
  const [tab, setTab] = useState<'references' | 'team'>('references')
  const [globalRefs, setGlobalRefs] = useState<RfpReferenceDoc[]>([])
  const [teamMembers, setTeamMembers] = useState<RfpTeamMember[]>([])
  const [routing, setRouting] = useState<RfpDefaultRouting[]>([])
  const [loading, setLoading] = useState(true)

  const loadAll = async () => {
    setLoading(true)
    const [refsRes, teamRes, routingRes] = await Promise.all([
      rfpApi.listGlobalReferences(),
      rfpApi.listTeam(),
      rfpApi.listRouting(),
    ])
    setGlobalRefs(unwrapList(refsRes) as RfpReferenceDoc[])
    setTeamMembers(unwrapList(teamRes) as RfpTeamMember[])
    setRouting(unwrapList(routingRes) as RfpDefaultRouting[])
    setLoading(false)
  }

  useEffect(() => {
    if (open) loadAll()
  }, [open])

  if (!open) return null

  const tabs = [
    { key: 'references' as const, label: 'Sample Responses' },
    { key: 'team' as const, label: 'Team & Routing' },
  ]

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-y-0 right-0 z-50 w-[600px] max-w-[90vw] bg-[var(--bg-primary)] border-l border-[var(--border-color)] shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)] shrink-0">
          <div className="flex items-center gap-2">
            <Settings size={16} className="text-indigo-400" />
            <h2 className="text-sm font-semibold text-[var(--text-primary)]">RFP Tool Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-secondary)] transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[var(--border-color)] shrink-0">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`flex-1 px-4 py-2.5 text-xs font-medium transition-colors ${
                tab === t.key
                  ? 'text-indigo-400 border-b-2 border-indigo-400'
                  : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>
          ) : tab === 'references' ? (
            <ReferenceUploader references={globalRefs} onUpdate={loadAll} />
          ) : (
            <div className="space-y-6">
              <TeamManager members={teamMembers} onUpdate={loadAll} />
              <div className="border-t border-[var(--border-color)]" />
              <RoutingConfig routing={routing} teamMembers={teamMembers} onUpdate={loadAll} />
            </div>
          )}
        </div>
      </div>
    </>
  )
}

function RoutingConfig({ routing, teamMembers, onUpdate }: { routing: RfpDefaultRouting[]; teamMembers: RfpTeamMember[]; onUpdate: () => void }) {
  const handleAssign = async (category: string, memberId: string) => {
    if (!memberId) {
      // Unassign
      await rfpApi.deleteRouting(category)
      showToast('Routing removed', 'success')
      onUpdate()
      return
    }
    const member = teamMembers.find(m => String(m.id) === memberId)
    if (!member) return
    await rfpApi.upsertRouting({ category, name: member.name, email: member.email })
    showToast(`${member.name} assigned to ${ROUTING_CATEGORIES.find(c => c.key === category)?.label || category}`, 'success')
    onUpdate()
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-xs font-semibold text-[var(--text-primary)] mb-1">Question Routing</h3>
        <p className="text-[11px] text-[var(--text-muted)]">
          Assign a team member to each question category. When requirements are tagged, they can be auto-assigned to the right person.
        </p>
      </div>

      {teamMembers.length === 0 ? (
        <p className="text-xs text-[var(--text-muted)] italic">Add team members above to configure routing.</p>
      ) : (
        <div className="space-y-1.5">
          {ROUTING_CATEGORIES.map(cat => {
            const assigned = routing.find(r => r.category === cat.key)
            const selectedMemberId = assigned
              ? String(teamMembers.find(m => m.email === assigned.email)?.id || '')
              : ''

            // Commercial routes to the project salesperson automatically — not a fixed person
            const isProjectSalesperson = cat.key === 'commercial'

            return (
              <div key={cat.key} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-[var(--bg-secondary)] transition-colors gap-3">
                <p className="text-xs font-medium text-[var(--text-primary)] shrink-0">{cat.label}</p>
                {isProjectSalesperson ? (
                  <span className="flex-1 max-w-[220px] px-2 py-1.5 text-xs text-indigo-400 italic text-right">
                    Project Salesperson
                  </span>
                ) : (
                  <select
                    value={selectedMemberId}
                    onChange={e => handleAssign(cat.key, e.target.value)}
                    className="flex-1 max-w-[220px] rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2 py-1.5 text-xs text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                  >
                    <option value="">Not assigned</option>
                    {teamMembers.map(m => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
